import React, { useState, useEffect } from "react";
import axios from "axios";
import { UserCheck, Plus, Trash2, Edit2, CheckCircle2, Shield, X, Handshake } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useMenuSettings } from "../hooks/useMenuSettings";
import { BUILTIN_ROLES } from "../config/modules";

export const PengaturanPenggunaPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [notification, setNotification] = useState("");
  const { user: currentUser } = useAuth();
  const { custom_roles } = useMenuSettings();
  const [mitraList, setMitraList] = useState([]);
  const [karyawanList, setKaryawanList] = useState([]);
  const [penarikList, setPenarikList] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const roleOptions = (() => {
    const base = [
      ...BUILTIN_ROLES.map((r) => ({ id: r.id, label: r.label })),
      { id: "investor", label: "Investor / Mitra" },
      ...(custom_roles || []).map((r) => ({ id: r.id, label: r.label })),
    ];
    const seen = new Set();
    return base.filter((r) => (seen.has(r.id) ? false : seen.add(r.id)));
  })();

  const emptyForm = {
    name: "",
    email: "",
    password: "",
    role: "teknisi",
    phone: "",
    status: "aktif",
    mitra_id: "",
    karyawan_id: "",
    penarik_id: "",
  };
  const [form, setForm] = useState(emptyForm);
  const mitraName = (id) => mitraList.find((m) => m.id === id)?.nama || null;
  const karyawanName = (id) => karyawanList.find((k) => k.id === id)?.nama || null;
  const penarikName = (id) => penarikList.find((p) => p.id === id)?.nama || null;

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/auth/users`, {
        withCredentials: true
      });
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMitra = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/master/mitra`, { withCredentials: true });
      setMitraList(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchKaryawan = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/master/karyawan`, { withCredentials: true });
      setKaryawanList(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPenarik = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/penarik`, { withCredentials: true });
      setPenarikList(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchMitra();
    fetchKaryawan();
    fetchPenarik();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowAdd(true);
  };

  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({
      name: u.name || "",
      email: u.email || "",
      password: "",
      role: u.role || "teknisi",
      phone: u.phone || "",
      status: u.status || "aktif",
      mitra_id: u.mitra_id || "",
      karyawan_id: u.karyawan_id || "",
      penarik_id: u.penarik_id || "",
    });
    setShowAdd(true);
  };

  const closeModal = () => {
    setShowAdd(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (payload.role === "investor") { payload.karyawan_id = ""; payload.penarik_id = ""; }
    else if (payload.role === "penarik") { payload.mitra_id = ""; payload.karyawan_id = ""; }
    else { payload.mitra_id = ""; payload.penarik_id = ""; }
    try {
      if (editingId) {
        if (!payload.password) delete payload.password;
        await axios.put(`${BACKEND_URL}/api/auth/users/${editingId}`, payload, { withCredentials: true });
        setNotification("Data pengguna berhasil diperbarui!");
      } else {
        await axios.post(`${BACKEND_URL}/api/auth/users`, payload, { withCredentials: true });
        setNotification("Pengguna baru berhasil didaftarkan!");
      }
      closeModal();
      fetchUsers();
      setTimeout(() => setNotification(""), 4000);
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal menyimpan user");
    }
  };

  const handleDeleteUser = async (id, name) => {
    if (window.confirm(`Hapus akun pengguna "${name}"?`)) {
      try {
        await axios.delete(`${BACKEND_URL}/api/auth/users/${id}`, {
          withCredentials: true
        });
        setNotification("User berhasil dihapus");
        fetchUsers();
        setTimeout(() => setNotification(""), 4000);
      } catch (err) {
        alert(err.response?.data?.detail || "Gagal menghapus user");
      }
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="pengaturan-pengguna-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-500/20 text-teal-400 flex items-center justify-center">
            <UserCheck className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Manajemen Pengguna & Hak Akses (RBAC)</h1>
            <p className="text-xs text-slate-400">
              Kelola akun staf Administrator, Kasir/Finance, dan Teknisi Lapangan
            </p>
          </div>
        </div>

        {currentUser?.role === "admin" && (
          <button
            onClick={openAdd}
            data-testid="add-user-btn"
            className="px-4 py-2 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-teal-600/30 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Tambah User Baru</span>
          </button>
        )}
      </div>

      {notification && (
        <div className="p-3 bg-teal-500/10 border border-teal-500/30 text-teal-300 rounded-xl text-xs flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-teal-400" />
          <span>{notification}</span>
        </div>
      )}

      {/* Users Table */}
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl overflow-hidden">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-slate-800 text-slate-400 font-semibold">
              <th className="py-2.5 px-3">Nama Lengkap</th>
              <th className="py-2.5 px-3">Email Login</th>
              <th className="py-2.5 px-3">Role Akses</th>
              <th className="py-2.5 px-3">Tertaut ke</th>
              <th className="py-2.5 px-3">No. Kontak</th>
              <th className="py-2.5 px-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-slate-900/40">
                <td className="py-3 px-3 font-bold text-white">{u.name}</td>
                <td className="py-3 px-3 font-mono text-cyan-300">{u.email}</td>
                <td className="py-3 px-3">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                      u.role === "admin"
                        ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                        : u.role === "finance"
                        ? "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                        : "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                    }`}
                  >
                    {u.role}
                  </span>
                </td>
                <td className="py-3 px-3">
                  {u.role === "investor" ? (
                    mitraName(u.mitra_id) ? (
                      <span data-testid={`user-mitra-${u.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                        <Handshake className="w-3 h-3" /> {mitraName(u.mitra_id)}
                      </span>
                    ) : (
                      <span className="text-[10px] text-rose-400 font-semibold">Belum ditautkan</span>
                    )
                  ) : karyawanName(u.karyawan_id) ? (
                    <span data-testid={`user-karyawan-${u.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/20 text-blue-300 border border-blue-500/30">
                      <UserCheck className="w-3 h-3" /> {karyawanName(u.karyawan_id)}
                    </span>
                  ) : (
                    <span className="text-slate-600">—</span>
                  )}
                </td>
                <td className="py-3 px-3 text-slate-300">{u.phone || "-"}</td>
                <td className="py-3 px-3 text-right">
                  {currentUser?.role === "admin" && (
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(u)}
                        data-testid={`edit-user-${u.id}`}
                        className="p-1.5 text-cyan-400 hover:bg-cyan-950/60 rounded-lg"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      {currentUser?.id !== u.id && (
                        <button
                          onClick={() => handleDeleteUser(u.id, u.name)}
                          className="p-1.5 text-rose-400 hover:bg-rose-950/60 rounded-lg"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-md p-5 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-sm font-bold text-white">{editingId ? "Edit Akun Pengguna" : "Tambah Akun Pengguna"}</h3>
              <button onClick={closeModal} className="text-slate-400">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmitUser} className="space-y-3">
              <div>
                <label className="block text-xs text-slate-300 mb-1">Nama Lengkap *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Contoh: Andi Pratama"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-300 mb-1">Email Login *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="andi@ispgateway.id"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                />
              </div>

              <div>
                <label className="block text-xs text-slate-300 mb-1">{editingId ? "Kata Sandi (kosongkan bila tidak diubah)" : "Kata Sandi *"}</label>
                <input
                  type="password"
                  required={!editingId}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Role Akses</label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                  >
                    {roleOptions.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-slate-300 mb-1">No. Telepon / WA</label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="0812345678"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-slate-300 mb-1">
                  Tautkan ke: {form.role === "investor" ? "Data Investor/Mitra *" : form.role === "penarik" ? "Data Penarik/Kolektor *" : "Data Karyawan"}
                </label>
                {form.role === "investor" ? (
                  <>
                    <select
                      required
                      value={form.mitra_id}
                      onChange={(e) => setForm({ ...form, mitra_id: e.target.value })}
                      data-testid="user-mitra-select"
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                    >
                      <option value="">— Pilih Investor —</option>
                      {mitraList.map((mtr) => (
                        <option key={mtr.id} value={mtr.id}>{mtr.nama} · {mtr.prosentase_sharing}% sharing</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 mt-1">
                      Akun investor hanya melihat data pelanggan & bagi hasil sesuai mitra yang ditautkan.
                      {mitraList.length === 0 && " (Belum ada data mitra — tambahkan di Master Data → Mitra/Investor)"}
                    </p>
                  </>
                ) : form.role === "penarik" ? (
                  <>
                    <select
                      required
                      value={form.penarik_id}
                      onChange={(e) => setForm({ ...form, penarik_id: e.target.value })}
                      data-testid="user-penarik-select"
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                    >
                      <option value="">— Pilih Penarik —</option>
                      {penarikList.map((p) => (
                        <option key={p.id} value={p.id}>{p.nama}{p.no_hp ? ` · ${p.no_hp}` : ""}</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 mt-1">
                      Akun penarik hanya melihat tagihan pelanggan di wilayahnya & mencatat pembayaran tunai.
                      {penarikList.length === 0 && " (Belum ada data penarik — tambahkan di Master Data → Penarik/Kolektor)"}
                    </p>
                  </>
                ) : (
                  <>
                    <select
                      value={form.karyawan_id}
                      onChange={(e) => setForm({ ...form, karyawan_id: e.target.value })}
                      data-testid="user-karyawan-select"
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                    >
                      <option value="">— Pilih Karyawan (opsional) —</option>
                      {karyawanList.map((k) => (
                        <option key={k.id} value={k.id}>{k.nama} · {k.jabatan}</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 mt-1">
                      Tautkan akun ke data karyawan sebagai identitas staf.
                      {karyawanList.length === 0 && " (Belum ada data karyawan — tambahkan di Master Data → Karyawan)"}
                    </p>
                  </>
                )}
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-xl text-xs"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  data-testid="submit-user-btn"
                  className="px-4 py-1.5 bg-teal-600 text-white rounded-xl text-xs font-bold"
                >
                  {editingId ? "Simpan Perubahan" : "Daftarkan User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
